/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-11
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from "react";

/**
 * Verrou de marque : le nom et sa signature, cales sur exactement la meme
 * largeur. Partage par les deux sites — le Tchad l'importe depuis `tchad/`.
 *
 * Le probleme : « Prenim » fait six lettres, « DIGITAL TRANSFORMATION » en fait
 * vingt-deux. A taille egale la signature est trois fois plus large, et tout
 * reglage au jugé se decale des qu'une police charge, qu'un ecran change de
 * taille ou qu'on traduit le libelle.
 *
 * La signature est donc composee lettre a lettre dans une ligne flex en
 * `justify-between` : les caracteres se repartissent d'eux-memes sur toute la
 * largeur du bloc, que le nom impose. L'alignement est exact par construction,
 * a n'importe quelle taille, sans valeur magique a maintenir. C'est la façon
 * dont se compose une signature de logotype.
 *
 * Seule contrainte : la signature doit tenir naturellement dans la largeur du
 * nom, sinon elle deborderait au lieu de s'etirer. D'ou `ratio`, la taille de
 * la signature exprimee en fraction de celle du nom — 0.22 pour « Prenim »,
 * 0.40 pour « Prenim Tchad », qui est bien plus large. Les deux tailles
 * derivent ainsi d'une seule : celle posee sur le conteneur par `className`,
 * ce qui rend le verrou homothetique d'un point d'arret a l'autre.
 */
/**
 * Hauteur totale du verrou, exprimee en `em` de la taille du nom.
 *
 * Elle sert a dimensionner le logo image pour qu'il fasse exactement la hauteur
 * du logotype. On ne peut pas s'en remettre a `align-self: stretch` : le fichier
 * du logo mesure plusieurs centaines de pixels de haut, et sans contrainte de
 * hauteur c'est LUI qui imposait sa taille intrinseque a toute la rangee.
 *
 * Les deux coefficients viennent d'une mesure dans le navigateur, sur les deux
 * rapports reellement utilises (0.22 au UK, 0.40 au Tchad), puis d'une droite
 * passant par ces deux points. Ils dependent des metriques d'Outfit, la police
 * d'affichage commune aux deux sites : changer de police, ou le rapport, oblige
 * a les reprendre.
 *
 * Aucun test automatique ne les garde pour l'instant. Pour les reverifier,
 * comparer dans le navigateur la hauteur du logo image a celle de ce bloc :
 * les deux doivent coincider a moins d'un pixel.
 */
export const lockupHeightEm = (ratio) => 0.9033 + 1.3356 * ratio;

const Wordmark = ({
  name,
  tagline,
  ratio = 0.22,
  className = "",
  nameClassName = "",
  taglineClassName = "",
  testId,
}) => (
  <span
    data-testid={testId}
    className={`inline-flex flex-col items-stretch leading-none ${className}`}
  >
    <span
      className={`wordmark-name font-bold tracking-tight ${nameClassName}`}
      style={{ fontSize: "1em", lineHeight: 0.95 }}
    >
      {name}
    </span>
    {/* La signature eclatee en lettres se lirait « D I G I T A L » a la
        synthese vocale : elle est donc masquee aux lecteurs d'ecran, qui
        recoivent le libelle entier juste avant. */}
    <span className="sr-only">{tagline}</span>
    {/* `dir="ltr"` est indispensable : la signature est un element de marque,
        toujours en caracteres latins. Sans lui, le document arabe passe la
        ligne flex en droite-a-gauche et les lettres se lisent a l'envers. */}
    <span
      aria-hidden="true"
      dir="ltr"
      className={`flex justify-between font-semibold uppercase ${taglineClassName}`}
      style={{ fontSize: `${ratio}em`, marginTop: `${ratio * 0.55}em` }}
    >
      {/* Chaque lettre est sa propre boite pour que `justify-between` puisse
          les ecarter. L'espace entre les mots recoit une largeur propre, sans
          quoi il se confondrait avec les intervalles entre lettres et les deux
          mots se liraient comme un seul. */}
      {Array.from(tagline).map((ch, i) =>
        ch === " " ? (
          <span key={i} className="w-[0.45em]" />
        ) : (
          <span key={i}>{ch}</span>
        )
      )}
    </span>
  </span>
);

export default Wordmark;
